import { create, useStore } from "zustand";
import { temporal } from "zundo";
import { toast } from "sonner";
import i18n from "@/i18n";
import {
  Block,
  CanvasDocumentContent,
  CanvasDocumentWithContent,
  createEmptyDocumentContent,
} from "./types";
import { createBlock } from "./types/helpers";
import type { BlockType as V2BlockType, Block as V2Block } from "./types/blocks";
import { saveDocumentWithVersion } from "./service";

// Autosave debounce time in milliseconds
const AUTOSAVE_DEBOUNCE_MS = 1500;

interface CanvasState {
  // Document state
  document: CanvasDocumentWithContent | null;
  content: CanvasDocumentContent;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;

  // Backend sync generation counter - incremented each time the backend
  // delivers authoritative content (syncFromBackend / setDocument).
  // Used to detect and correct stale in-flight saves.
  _backendSyncGen: number;

  // When true, save() and scheduleSave() are completely blocked.
  // Set by the chat agent flow to prevent autosave from overwriting
  // server-side tool writes.
  _agentEditingLock: boolean;
  setAgentEditingLock: (locked: boolean) => void;

  // Preview mode state
  isPreviewMode: boolean;
  previewVersionId: string | null;
  previewContent: CanvasDocumentContent | null;
  originalContent: CanvasDocumentContent | null;

  // Block minimize state (UI-only, not persisted)
  minimizedBlocks: Set<string>;

  // Autosave
  saveTimeoutId: NodeJS.Timeout | null;
  autosaveUserId: string | null;

  // Actions
  setDocument: (document: CanvasDocumentWithContent) => void;
  syncFromBackend: (content: CanvasDocumentContent) => void;
  clearDocument: () => void;

  // Block operations
  updateBlock: (blockId: string, updates: Partial<V2Block>) => void;
  updateBlockContent: (blockId: string, content: Block["content"]) => void;
  addBlock: (type: V2BlockType, afterBlockId?: string) => void;
  removeBlock: (blockId: string) => void;
  reorderBlocks: (blockIds: string[]) => void;

  // Block minimize operations
  toggleBlockMinimized: (blockId: string) => void;
  minimizeAllBlocks: () => void;
  expandAllBlocks: () => void;

  // Content operations
  setContent: (content: CanvasDocumentContent) => void;

  // Save operations
  save: (userId?: string) => Promise<void>;
  scheduleSave: (userId?: string) => void;
  cancelPendingSave: () => void;
  setAutosaveUserId: (userId: string | null) => void;

  // Preview mode actions
  enterPreviewMode: (versionId: string, content: CanvasDocumentContent) => void;
  exitPreviewMode: () => void;
  restorePreviewedVersion: () => void;
}

export const useCanvasStore = create<CanvasState>()(
  temporal(
    (set, get) => ({
      // Initial state
      document: null,
      content: createEmptyDocumentContent(),
      isDirty: false,
      isSaving: false,
      lastSavedAt: null,
      _backendSyncGen: 0,
      _agentEditingLock: false,
      saveTimeoutId: null,
      autosaveUserId: null,

      // Preview mode initial state
      isPreviewMode: false,
      previewVersionId: null,
      previewContent: null,
      originalContent: null,

      // Block minimize initial state
      minimizedBlocks: new Set<string>(),

      // Set document from server
      setDocument: (document) => {
        // Cancel any pending save when loading new document
        get().cancelPendingSave();

        set((state) => ({
          document,
          content: document.content,
          isDirty: false,
          lastSavedAt: new Date(document.updated_at),
          // Increment generation to invalidate any in-flight saves
          _backendSyncGen: state._backendSyncGen + 1,
        }));

        // Clear temporal history so loaded document becomes the baseline
        useCanvasStore.temporal.getState().clear();
      },

      // Sync content from backend (AI tool updates) without clearing undo history
      syncFromBackend: (content) => {
        // Cancel any pending autosave (backend already saved)
        get().cancelPendingSave();

        set((state) => ({
          content,
          isDirty: false, // Backend save is authoritative
          // Increment generation to invalidate any in-flight saves
          _backendSyncGen: state._backendSyncGen + 1,
        }));
        // NOTE: Do NOT clear temporal history - this preserves undo capability
      },

      setAgentEditingLock: (locked) => {
        if (locked) {
          // When locking, also cancel any pending save
          get().cancelPendingSave();
        }
        set({ _agentEditingLock: locked, isDirty: false });
      },

      // Clear document state
      clearDocument: () => {
        get().cancelPendingSave();

        set({
          document: null,
          content: createEmptyDocumentContent(),
          isDirty: false,
          isSaving: false,
          lastSavedAt: null,
        });
      },

      // Update a block
      updateBlock: (blockId, updates) => {
        set((state) => {
          const blocks = state.content.blocks.map((block) =>
            block.id === blockId ? ({ ...block, ...updates } as Block) : block,
          );
          return {
            content: { ...state.content, blocks },
            isDirty: true,
          };
        });

        // Schedule autosave
        get().scheduleSave();
      },

      // Update block content specifically
      updateBlockContent: (blockId, content) => {
        set((state) => {
          const blocks = state.content.blocks.map((block) =>
            block.id === blockId ? { ...block, content } : block,
          );
          return {
            content: { ...state.content, blocks },
            isDirty: true,
          };
        });

        // Schedule autosave
        get().scheduleSave();
      },

      // Add a new block
      addBlock: (type, afterBlockId) => {
        set((state) => {
          const blocks = [...state.content.blocks];

          // Determine order for new block
          let insertIndex = blocks.length;
          if (afterBlockId) {
            const afterIndex = blocks.findIndex((b) => b.id === afterBlockId);
            if (afterIndex !== -1) {
              insertIndex = afterIndex + 1;
            }
          }

          const newBlock = createBlock(type, insertIndex) as unknown as Block;

          // Insert block and update order of subsequent blocks
          blocks.splice(insertIndex, 0, newBlock);

          // Recompute order for all blocks
          const reorderedBlocks = blocks.map((block, index) => ({
            ...block,
            order: index,
          }));

          return {
            content: { ...state.content, blocks: reorderedBlocks },
            isDirty: true,
          };
        });

        // Schedule autosave
        get().scheduleSave();
      },

      // Remove a block
      removeBlock: (blockId) => {
        set((state) => {
          const blocks = state.content.blocks
            .filter((block) => block.id !== blockId)
            .map((block, index) => ({ ...block, order: index }));

          return {
            content: { ...state.content, blocks },
            isDirty: true,
          };
        });

        // Schedule autosave
        get().scheduleSave();
      },

      // Reorder blocks
      reorderBlocks: (blockIds) => {
        set((state) => {
          const blockMap = new Map(state.content.blocks.map((block) => [block.id, block]));

          const blocks = blockIds
            .map((id, index) => {
              const block = blockMap.get(id);
              return block ? { ...block, order: index } : null;
            })
            .filter((block): block is Block => block !== null);

          return {
            content: { ...state.content, blocks },
            isDirty: true,
          };
        });

        // Schedule autosave
        get().scheduleSave();
      },

      // Toggle block minimized state
      toggleBlockMinimized: (blockId) => {
        set((state) => {
          const newMinimized = new Set(state.minimizedBlocks);
          if (newMinimized.has(blockId)) {
            newMinimized.delete(blockId);
          } else {
            newMinimized.add(blockId);
          }
          return { minimizedBlocks: newMinimized };
        });
      },

      // Minimize all blocks
      minimizeAllBlocks: () => {
        set((state) => {
          const allBlockIds = state.content.blocks.map((b) => b.id);
          return { minimizedBlocks: new Set(allBlockIds) };
        });
      },

      // Expand all blocks
      expandAllBlocks: () => {
        set({ minimizedBlocks: new Set<string>() });
      },

      // Set content directly (for bulk updates)
      setContent: (content) => {
        set({ content, isDirty: true });
        get().scheduleSave();
      },

      // Save document to server with version
      save: async (userId) => {
        const state = get();

        if (!state.document || !state.isDirty || state._agentEditingLock) {
          return;
        }

        // Capture content and backend sync generation at start of save.
        // If the generation changes during the async save, it means the backend
        // delivered authoritative content (via syncFromBackend) while we were
        // writing stale content -- we must correct the overwrite.
        const contentAtSaveStart = state.content;
        const genAtSaveStart = state._backendSyncGen;
        const documentId = state.document.id;

        set({ isSaving: true });

        try {
          await saveDocumentWithVersion(documentId, contentAtSaveStart, userId);

          const afterState = get();

          // Check if backend delivered new authoritative content during our save.
          // If so, our save just overwrote it with stale content -- we need to
          // immediately re-save the current (authoritative) content to correct this.
          if (afterState._backendSyncGen !== genAtSaveStart) {
            console.warn(
              "[canvas/store] Backend sync occurred during save - correcting stale overwrite",
            );
            set({ isSaving: false });

            // The current store content is the authoritative backend content.
            // If it differs from what we just wrote, re-save to correct the DB.
            if (afterState.document && afterState.content !== contentAtSaveStart) {
              // Mark dirty and immediately save the authoritative content
              set({ isDirty: true });
              // Save immediately (no debounce) to minimize the window of stale data
              await get().save(userId);
            }
            return;
          }

          // Normal path: no backend sync occurred during save
          // Only clear isDirty if content hasn't changed during save
          // This prevents losing edits made while save was in progress
          const currentContent = afterState.content;
          const contentUnchanged = currentContent === contentAtSaveStart;

          set({
            isDirty: contentUnchanged ? false : true,
            isSaving: false,
            lastSavedAt: new Date(),
          });

          // If content changed during save, schedule another save
          if (!contentUnchanged) {
            get().scheduleSave(userId);
          }
        } catch (error) {
          console.error("Failed to save document:", error);
          set({ isSaving: false });

          toast.error(i18n.t("canvas.errors.saveFailed", "Failed to save changes"));
          throw error;
        }
      },

      // Schedule autosave with debounce
      scheduleSave: (userId) => {
        const state = get();

        // Block autosave entirely while the agent is editing
        if (state._agentEditingLock) return;

        // Cancel any existing timeout
        if (state.saveTimeoutId) {
          clearTimeout(state.saveTimeoutId);
        }

        // Schedule new save
        const timeoutId = setTimeout(() => {
          const latestUserId = userId ?? get().autosaveUserId;
          get().save(latestUserId || undefined);
        }, AUTOSAVE_DEBOUNCE_MS);

        set({ saveTimeoutId: timeoutId });
      },

      // Cancel pending save
      cancelPendingSave: () => {
        const state = get();
        if (state.saveTimeoutId) {
          clearTimeout(state.saveTimeoutId);
          set({ saveTimeoutId: null });
        }
      },

      // Set the default userId for autosave
      setAutosaveUserId: (userId) => {
        set({ autosaveUserId: userId });
      },

      // Enter preview mode - show a historical version
      enterPreviewMode: (versionId, content) => {
        const state = get();
        // Cancel any pending save before preview
        state.cancelPendingSave();

        set({
          isPreviewMode: true,
          previewVersionId: versionId,
          previewContent: content,
          // Save current content so we can restore it on exit
          originalContent: state.content,
        });
      },

      // Exit preview mode - return to current version
      exitPreviewMode: () => {
        const state = get();
        set({
          isPreviewMode: false,
          previewVersionId: null,
          previewContent: null,
          // Restore original content
          content: state.originalContent || state.content,
          originalContent: null,
        });
      },

      // Restore previewed version as the current content
      restorePreviewedVersion: () => {
        const state = get();
        if (!state.previewContent) return;

        // Exit preview mode but keep the preview content as the new content
        set({
          isPreviewMode: false,
          previewVersionId: null,
          content: state.previewContent, // Make preview content the current
          previewContent: null,
          originalContent: null,
          isDirty: true, // Mark as dirty to trigger save
        });

        // Schedule save to persist the restored version
        get().scheduleSave();
      },
    }),
    {
      // Track only content changes (not UI state like isDirty, isSaving)
      partialize: (state) => ({ content: state.content }),
      // Limit to 50 undo states (exceeds 40+ requirement)
      limit: 50,
      // Equality function to avoid duplicate states
      equality: (pastState, currentState) =>
        JSON.stringify(pastState.content) === JSON.stringify(currentState.content),
    },
  ),
);

// Hook to access undo/redo state and actions
export const useCanvasHistory = () => {
  const temporalStore = useCanvasStore.temporal;
  const canUndo = useStore(temporalStore, (state) => state.pastStates.length > 0);
  const canRedo = useStore(temporalStore, (state) => state.futureStates.length > 0);
  const undo = useStore(temporalStore, (state) => state.undo);
  const redo = useStore(temporalStore, (state) => state.redo);
  return { canUndo, canRedo, undo, redo };
};

// Export temporal controls for AI batch operations (pause/resume)
export const getTemporalControls = () => useCanvasStore.temporal.getState();

// Hook to get current user ID for save operations (use with AuthContext)
// Usage: const userId = useAuth().user?.id; store.scheduleSave(userId);
